'use strict';

const _ = require('lodash');
const bowser = require('bowser');
const Promise = require('bluebird');
const Api = require('./Api');
const Helper = require('./Helper');
const Connection = require('./Connection');

/**
 * A utility that will diagnose the state of the system and detect problems in it
 *
 * A Diagnose check will return an object with the following parameters:
 * ok: Whether the check succeeded or not
 * errorCode: (optional) The error code of why the check failed
 * level: (optional) The level of "failure", whether the check failed completely or not (error or warning)
 * Other properties may be returned with additional information for specific tests
 */
class Diagnoser {

    static get typeAgent () {
        return 'agent';
    }

    static get typeIntegration () {
        return 'integration';
    }

    static get allMode () {
        return 'all';
    }

    static get criticalMode () {
        return 'critical';
    }

    static get nonCriticalMode () {
        return 'nonCritical';
    }

    /**
     *
     * @param {string} type agent | integration
     */
    static getCriticalChecks (type) {
        switch (type) {
        case Diagnoser.typeAgent:
            return ['checkBrowserConnection', 'checkDomainAccess', 'checkAgentRooms'];
        case Diagnoser.typeIntegration:
            return ['checkDomainAccess'];
        default:
            break;
        }
    }

    /**
     *
     * @param {string} type agent | integration
     */
    static getNonCriticalChecks (type) {
        switch (type) {
        case Diagnoser.typeAgent:
            return ['checkBrowserSupport', 'checkTimeSync', 'checkPing'];
        case Diagnoser.typeIntegration:
            return ['checkThirdPartyCookies', 'checkBrowserSupport', 'checkTimeSync', 'checkPing', 'checkWebsockets'];
        default:
            break;
        }
    }

    /**
     * Check state for an agent usage
     * @param {string} mode all | critical | nonCritical
     * @param {object} options optional object with configuration
     */
    static checkForAgent (mode, options = null) {
        return Diagnoser.check(mode, Diagnoser.typeAgent, options);
    }

    /**
     * Check state for an integration usage
     * @param {string} mode all | critical | nonCritical
     * @param {object} options optional object with configuration
     */
    static checkForIntegration (mode, options = null) {
        return Diagnoser.check(mode, Diagnoser.typeIntegration, options);
    }

    /**
     * Check state for an integration usage
     * @param {string} mode all | critical | nonCritical
     * @param {string} type agent | integration
     * @param {object} options optional object with configuration
     */
    static check (mode, type, options = null) {
        switch (mode) {
        case Diagnoser.allMode:
            return Diagnoser.checkAll(type, options);
        case Diagnoser.criticalMode:
            return Diagnoser.checkCritical(type, options);
        case Diagnoser.nonCriticalMode:
            return Diagnoser.checkNonCritical(type, options);
        default:
            break;
        }
    }

    /**
     * Check every condition
     * @param {string} type agent | integration
     * @param {object} options optional object with configuration
     */
    static checkAll (type, options = null) {
        return Promise.all([Diagnoser.checkCritical(type, options), Diagnoser.checkNonCritical(type, options)])
            .then(([resCritical, resNonCritical]) => {
                return [...resCritical, ...resNonCritical];
            });
    }

    /**
     * Check only conditions considered to be critical
     * @param {string} type agent | integration
     * @param {object} options optional object with configuration
     */
    static checkCritical (type, options = null) {
        return Diagnoser._checkGroup(Diagnoser.getCriticalChecks(type), type, options)
            .then(res => {
                return res.map(item => {
                    item.critical = true;
                    return item;
                });
            });

    }

    /**
     * Check only conditions considered to be non-critical
     * @param {string} type agent | integration
     * @param {object} options optional object with configuration
     */
    static checkNonCritical (type, options = null) {
        return Diagnoser._checkGroup(Diagnoser.getNonCriticalChecks(type), type, options);
    }

    /**
     * Check a group of conditions passed by name
     * @param {Array} names
     * @param {string} type agent | integration
     * @param {object} options optional object with configuration
     * @returns {Array}
     */
    static _checkGroup (names, type, options = null) {
        return Promise.map(names, name => {
            const args = [type];
            if (options && options[name] && options[name].data) {
                args.push(options[name].data);
            }
            return Diagnoser[name](...args)
                .then(res => {
                    res.name = name;
                    return res;
                });
        });
    }

    /**
     * @param {string} type agent | integration
     */
    static _getBrowserSupport (type) {
        switch (type) {
        case Diagnoser.typeAgent:
            return {
                'Chrome': '63',
                'Firefox': '52'
            };
        case Diagnoser.typeIntegration:
            return {
                'Chrome': '63',
                'Firefox': '52',
                'Internet Explorer': '11',
                'Microsoft Edge': '15',
                'Safari': '11.1',
                'iOS': '11.3'
            };
        default:
            break;
        }
    }

    /**
     * Check if the current browser is supported
     * @param {string} type agent | integration
     * @returns {object} { ok, message (only if !ok), validBrowsers (only if !ok) }
     */
    static checkBrowserSupport (type) {
        const validBrowsers = Diagnoser._getBrowserSupport(type);
        const errorCode = 'BROWSER_NOT_SUPPORTED';
        try {
            const userAgent = bowser.getParser(navigator.userAgent).parse().parsedResult;
            let browser = userAgent.browser;

            // If it's an iOS Safari, compare using the OS version
            if (browser.name === 'Safari' && userAgent.os.name === 'iOS') {
                browser = userAgent.os;
            }

            const res = {
                ok: (validBrowsers[browser.name] && browser.version >= validBrowsers[browser.name]) || false
            };
            if (!res.ok) {
                res.errorCode = errorCode;
                res.validBrowsers = validBrowsers;
            }
            return Promise.resolve(res);
        } catch (e) {
            return Promise.resolve({ ok: false, errorCode, validBrowsers });
        }
    }

    /**
     * Check if third party cookies are enabled
     */
    static checkThirdPartyCookies () {
        return Helper.addScript(Api.getFullPath('/thirdPartyCookie'))
            .then(() => Helper.addScript(Api.getFullPath('/thirdPartyCookie/check')))
            .then(() => {
                return { ok: global.thirdPartyCookies };
            });
    }

    static checkWebsocketSupport () {
        return (global.WebSocket && global.WebSocket.CLOSING === 2);
    }

    /**
     * CHeck if the browser connection is valid (the browser supports WebSockets and/or third party cookies)
     */
    static checkBrowserConnection () {
        return Promise.resolve()
            .then(() => {
                if (Diagnoser.checkWebsocketSupport()) {
                    return { ok: true, messageCode: 'USING_WEBSOCKETS' };
                }
                return Diagnoser.checkThirdPartyCookies()
                    .then(res => {
                        if (res.ok) {
                            return { ok: false, errorCode: 'USING_LONG_POLLING', level: 'warning'};
                        } else {
                            return { ok: false, errorCode: 'INSUFFICIENT_BROWSER_CONNECTION'};
                        }
                    });
            });
    }

    /**
     * Check accessibility to inbenta domains
     */
    static checkDomainAccess () {
        const resOk = { ok: true };
        const resInacessible = { ok: false, errorCode: 'SERVICE_INACCESSIBLE' };
        const resTrouble = { ok: false, errorCode: 'SERVICE_NOT_WORKING' };

        return Diagnoser.apiGet('/health')
            .then(healthRes => {
                if (healthRes && healthRes.status === 200) {
                    return resOk;
                }
                return resInacessible;
            })
            .catch(e => {
                if (e.response && e.response.status && e.response.status >= 500) {
                    return resTrouble;
                }
            });
    }

    static apiGet (path) {
        return Api.request(path, 'GET');
    }

    /**
     * Check if the user time is in sync with the internet time
     */
    static checkTimeSync () {
        const errorCode = 'TIME_NOT_IN_SYNC';
        const res = {
            ok: Connection.timeDifferenceWithServer <= 3
        };
        if (!res.ok) {
            res.errorCode = errorCode;
            res.timeDifferenceWithServer = Connection.timeDifferenceWithServer;
        }
        return Promise.resolve(res);

    }

    /**
     * Check if the user ping is OK
     */
    static checkPing () {
        const maxRecommendedPing = 200;
        const errorCode = 'PING_TOO_HIGH';
        const res = {
            ok: Connection.ping <= maxRecommendedPing
        };
        if (!res.ok) {
            res.errorCode = errorCode;
            res.ping = Connection.ping;
            res.maxRecommendedPing = maxRecommendedPing;

        }
        return Promise.resolve(res);
    }

    /**
     * Check if websockets are enabled
     */
    static checkWebsockets () {
        if (!Diagnoser.checkWebsocketSupport()) {
            return Promise.resolve({
                ok: false,
                errorCode: 'WEBSOCKETS_NOT_SUPPORTED'
            });
        }

        const res = {
            ok: (Connection._conn && Connection._conn._socket.io.engine.transport.name === 'websocket')
        };
        if (!res.ok) {
            res.errorCode = 'WEBSOCKETS_NOT_ENABLED';
        }
        return Promise.resolve(res);
    }

    static checkAgentRooms (type, data = {}) {
        let appRooms = data.appRooms;
        let agentRooms = data.agentRooms;
        let pRooms = Promise.resolve();
        if (!appRooms) {
            pRooms = Diagnoser.apiGet('/settings')
                .then(res => {
                    if (!res) {
                        return { ok: false, errorCode: 'SERVICE_INACCESSIBLE' };
                    }
                    const data = res.data;
                    appRooms = _.get(data, 'settings.rooms');
                });
        }

        return pRooms
            .then(notOkRes => {
                if (notOkRes) {
                    return notOkRes;
                }

                if (!agentRooms) {
                    const me = Connection.me();
                    agentRooms = _.get(me, 'restrictions.rooms');
                }

                if (Array.isArray(appRooms) && Array.isArray(agentRooms)) {
                    const missingRooms = agentRooms.reduce((missing, room) => {
                        if (!appRooms.includes(room)) {
                            missing.push(room);
                        }
                        return missing;
                    }, []);
                    if (missingRooms.length) {
                        const level = missingRooms.length === agentRooms.length ? 'error' : 'warning';
                        return { ok: false, errorCode: 'ROOMS_NOT_CONFIGURED', missingRooms, level };
                    } else {
                        return { ok: true };
                    }
                } else {
                    return { ok: false, errorCode: 'UNKNOWN_ERROR' };
                }
            });
    }
}
module.exports = Diagnoser;



// WEBPACK FOOTER //
// ./src/Diagnoser.js